<script setup lang="ts">
import comingSoonBrewing from "@/assets/illustrations/coming-soon-brewing.svg?url";
import { useProductNoun } from "@/composition/useProductNoun";
import { useLinkedWorkflows } from "@/composables/useLinkedWorkflows";
import { useAppStore } from "@/store/app";
import type { LinkedWorkflow } from "@/types/linkedWorkflows";
import { Dataflow01Icon, PlusIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UISkeleton,
  UITabPane,
  UITabs,
  UITag,
  UITextMdMedium,
} from "@gohighlevel/ghl-ui";
import { computed, h, onActivated, onDeactivated, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import DeleteConfirmationModal from "../DeleteConfirmationModal.vue";
import LinkedWorkflowsTable from "./connections/LinkedWorkflowsTable.vue";
import RenameWorkflowModal from "./connections/RenameWorkflowModal.vue";

const store = useAppStore();
const { t } = useI18n();
const { assetParams } = useProductNoun();
const list = useLinkedWorkflows();
const hasWorkflowScope = computed(() => store.linkedWorkflowsScope !== null);
const canCreateWorkflow = computed(() => store.canOpenWorkflowHotlinks);
type ConnectionTab = "workflow" | "funnel" | "website";
const activeTab = ref<ConnectionTab>("workflow");

const workflowCount = computed(() =>
  hasWorkflowScope.value && list.status.value === "ready" ? list.count.value : 0
);

// Funnel and Website stay badge-less until they have real counts
const workflowTabLabel = () =>
  h("span", { class: "connections-pane__tab-label" }, [
    t("common.workflow"),
    workflowCount.value > 0
      ? h(
          "span",
          {
            class: "connections-pane__tab-count",
            "data-testid": "connections-tab-count-workflow",
          },
          String(workflowCount.value)
        )
      : null,
  ]);

const tabs = computed(() => [
  { value: "workflow" as const, label: workflowTabLabel },
  { value: "funnel" as const, label: t("common.funnel") },
  { value: "website" as const, label: t("common.website") },
]);

// Funnel and Website have no data yet: a neutral placeholder, never the workflow copy
const showComingSoon = computed(() => activeTab.value !== "workflow" || !hasWorkflowScope.value);
const showList = computed(
  () => hasWorkflowScope.value && activeTab.value === "workflow"
);
// "idle" is the moment before the first load starts -- not "no workflows"
const isLoading = computed(
  () =>
    showList.value && (list.status.value === "loading" || list.status.value === "idle")
);
const hasError = computed(() => showList.value && list.status.value === "error");
const hasRows = computed(
  () => showList.value && list.workflows.value.length > 0
);

// Visibility is separate from the target row, so a closing modal keeps its content through
// the leave transition; the row is replaced on the next open
const pendingRename = ref<LinkedWorkflow | null>(null);
const pendingDelete = ref<LinkedWorkflow | null>(null);
const showRename = ref(false);
const showDelete = ref(false);

function startRename(workflow: LinkedWorkflow) {
  pendingRename.value = workflow;
  showRename.value = true;
}

function startDelete(workflow: LinkedWorkflow) {
  pendingDelete.value = workflow;
  showDelete.value = true;
}

// Shared delete confirmation -- only the text is workflow-specific
const deleteDescription = computed(() =>
  pendingDelete.value
    ? t("common.deleteWorkflow.confirmDescription", { name: pendingDelete.value.name })
    : ""
);

// This pane already lists the linked workflows, so the host modal hides its own copy
function openHotlinks() {
  store.openWorkflowHotlinks({ hideWorkflowList: true });
}

// Only ids from the list fetched for this location and asset go to the host
function openWorkflow(workflow: LinkedWorkflow) {
  if (!list.isOwned(workflow.id)) return;
  store.openLinkedWorkflow(workflow.id, workflow.effectiveBuilder);
}

// Synchronous on purpose: window.open must stay inside the click's user gesture
function openWorkflowInNewTab(workflow: LinkedWorkflow) {
  if (!list.isOwned(workflow.id)) return;
  store.openLinkedWorkflowInNewTab(workflow.id, workflow.effectiveBuilder);
}

// Modals close on submit: the list already shows the optimistic result
function confirmRename(name: string) {
  showRename.value = false;
  if (pendingRename.value) void list.rename(pendingRename.value.id, name);
}

function confirmDelete() {
  showDelete.value = false;
  if (pendingDelete.value) void list.remove(pendingDelete.value.id);
}

// The host handshake can land after mount, so load as soon as the asset becomes eligible
watch(
  hasWorkflowScope,
  (eligible) => {
    if (eligible && list.status.value === "idle") void list.load();
  },
  { immediate: true }
);

// Integrate keeps panes alive: pause refreshes while hidden, catch up when shown again
onActivated(() => list.setActive(true));
onDeactivated(() => list.setActive(false));
</script>

<template>
  <section
    data-testid="connections-pane"
    class="connections-pane connections-pane--scrollable"
  >
    <header class="connections-pane__header">
      <div class="connections-pane__title">
        <h2>{{ t("common.connections") }}</h2>
        <UITag
          v-if="!hasWorkflowScope"
          type="info"
          size="small"
          :bordered="false"
        >
          {{ t("common.comingSoon") }}
        </UITag>
      </div>
    </header>
    <div class="connections-pane__tabs-row">
      <div class="connections-pane__tabs">
        <UITabs
          v-model:value="activeTab"
          type="segment"
          size="small"
          justify-content="start"
          :aria-label="t('common.connections')"
        >
          <UITabPane
            v-for="tab in tabs"
            :key="tab.value"
            :name="tab.value"
            :tab="tab.label"
          >
            <span
              class="connections-pane__tab-placeholder"
              aria-hidden="true"
            />
          </UITabPane>
        </UITabs>
      </div>
      <UIButton
        v-if="canCreateWorkflow && hasRows"
        id="connections-new-workflow"
        type="primary"
        size="small"
        @click="openHotlinks"
        ><PlusIcon class="connections-pane__button-icon" />{{ t("common.newWorkflow") }}</UIButton
      >
    </div>
    <div
      v-if="showComingSoon"
      data-testid="connections-coming-soon"
      class="connections-pane__coming-soon"
    >
      <img :src="comingSoonBrewing" alt="" class="connections-pane__illustration" />
      <UITextMdMedium class="text-base font-semibold leading-6 tracking-normal text-center">
        {{ t("common.comingSoon") }}
      </UITextMdMedium>
    </div>
    <div
      v-else-if="isLoading"
      data-testid="connections-workflows-loading"
      class="connections-pane__list"
    >
      <UISkeleton text :repeat="3" />
    </div>
    <div v-else-if="hasError" class="connections-pane__error">
      <p>{{ t("common.workflowsLoadError") }}</p>
      <UIButton
        id="connections-workflows-retry"
        type="tertiary"
        size="small"
        @click="list.load()"
        >{{ t("common.retry") }}</UIButton
      >
    </div>
    <div v-else-if="hasRows" class="connections-pane__list">
      <LinkedWorkflowsTable
        :workflows="list.workflows.value"
        :truncated="list.isTruncated.value"
        @open="openWorkflow"
        @open-new-tab="openWorkflowInNewTab"
        @rename="startRename"
        @delete="startDelete"
      />
    </div>
    <div v-else class="connections-pane__empty">
      <Dataflow01Icon class="h-6 w-6 text-primary-600" />
      <h3>{{ t("common.notConnectedToWorkflow", assetParams) }}</h3>
      <p>{{ t("common.workflowEmptyDescription", assetParams) }}</p>
      <UIButton
        v-if="canCreateWorkflow"
        id="connections-empty-new-workflow"
        type="primary"
        size="small"
        @click="openHotlinks"
        ><PlusIcon class="connections-pane__button-icon" />{{ t("common.newWorkflow") }}</UIButton
      >
    </div>
    <RenameWorkflowModal
      :show="showRename"
      :workflow="pendingRename"
      @rename="confirmRename"
      @cancel="showRename = false"
    />
    <DeleteConfirmationModal
      :show="showDelete"
      :title="t('common.deleteWorkflow.title')"
      :description="deleteDescription"
      @confirm="confirmDelete"
      @cancel="showDelete = false"
    />
  </section>
</template>

<style scoped>
.connections-pane {
  display: flex;
  min-height: 0;
  height: 100%;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: var(--base-white, #fff);
}
.connections-pane--scrollable {
  overflow-y: auto;
}
.connections-pane__header,
.connections-pane__title,
.connections-pane__tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.connections-pane__header {
  min-height: 58px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--gray-200, #eaecf0);
  box-sizing: border-box;
}
.connections-pane__title h2 {
  margin: 0;
  color: var(--gray-900, #101828);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}
.connections-pane__title {
  justify-content: flex-start;
}
.connections-pane__title :deep(.n-tag) {
  background: var(--primary-50, #eff4ff);
  color: var(--primary-700, #004eeb);
}
.connections-pane__tabs-row {
  margin-top: 12px;
  padding: 0 16px;
}
.connections-pane__button-icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
}
.connections-pane__tabs {
  display: inline-flex;
  width: max-content;
  max-width: 100%;
}
.connections-pane__tab-placeholder {
  display: none;
}
/* The tab label is rendered by UITabs, so the badge needs :deep() to be styled (U2) */
.connections-pane__tabs :deep(.connections-pane__tab-label) {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.connections-pane__tabs :deep(.connections-pane__tab-count) {
  display: inline-flex;
  min-width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border-radius: 6px;
  background: var(--gray-100, #f2f4f7);
  color: var(--gray-700, #344054);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}
.connections-pane__tabs :deep(.n-tabs-tab--active .connections-pane__tab-count) {
  background: var(--primary-50, #eff4ff);
  color: var(--primary-700, #004eeb);
}
.connections-pane__tabs :deep(.n-tabs),
.connections-pane__tabs :deep(.n-tabs-nav),
.connections-pane__tabs :deep(.n-tabs-rail) {
  width: max-content;
  max-width: 100%;
}
.connections-pane__tabs :deep(.n-tabs-rail) {
  gap: 2px;
  padding: 4px;
  border-radius: 8px;
  background: var(--gray-100, #f2f4f7);
}
.connections-pane__tabs :deep(.n-tabs-tab-wrapper) {
  flex: 0 0 auto;
}
.connections-pane__tabs :deep(.n-tabs-tab) {
  min-height: 32px;
  padding: 6px 12px;
  border-radius: 6px;
  color: var(--gray-600, #475467);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.connections-pane__tabs :deep(.n-tabs-tab__label) {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.connections-pane__tabs :deep(.n-tabs-capsule) {
  border-radius: 6px;
  background: var(--base-white, #fff);
  box-shadow: 0 1px 2px rgb(16 24 40 / 6%), 0 1px 3px rgb(16 24 40 / 10%);
}
.connections-pane__tabs :deep(.n-tabs-tab--active) {
  color: var(--primary-700, #004eeb);
}
.connections-pane__tabs :deep(.n-tabs-tab--active .n-tabs-tab__label) {
  font-weight: 500;
}
/* The table sits straight under the tabs row (U7) */
.connections-pane__list {
  margin-top: 16px;
  padding: 0 16px;
}
.connections-pane__error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 0 16px;
  color: var(--gray-600, #475467);
  font-size: 14px;
}
.connections-pane__error p {
  margin: 0;
}
.connections-pane__coming-soon {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  padding: 16px;
  color: var(--gray-500, #667085);
  text-align: center;
}
.connections-pane__illustration {
  width: min(294px, 100%);
  max-height: 50vh;
  object-fit: contain;
}
.connections-pane__empty {
  display: flex;
  flex: 1;
  max-width: 520px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-self: center;
  padding: 16px;
  text-align: center;
}
.connections-pane__empty h3 {
  margin: 16px 0 4px;
  font-size: 14px;
  font-weight: 600;
}
.connections-pane__empty p {
  margin: 0 0 16px;
  color: var(--gray-600, #475467);
  font-size: 12px;
}
</style>
